import type { MultiPolygon, Polygon } from 'geojson';
import { mergeWith, set, toArray } from 'lodash';
import { DateTime } from 'luxon';
import { proxy } from 'valtio';
import { geomToMultiPolygon } from '~/common/geoUtils';
import { compareWithArrayCopy } from '~/common/storeUtils';
import {
  EditorMode,
  type MapDetail,
  type MapObject,
  MapStyle,
  ProjectGeometryType,
  type TLoadedState,
  type TLoadingState,
  type UnitSystem,
} from '~/common/types';
import { MapStoreBase } from '~/components2/Map';
import { geometryToPolygonObjects } from './draw/helpers';

function freshMap(): TLoadedState & MapDetail {
  return {
    isLoading: false,
    id: 'sub-geometry-draw',
    name: 'sub-geometry-draw',
    image: '',
    unitSystem: 'imperial',
    baseMap: MapStyle.streets,
    createdAt: DateTime.now(),
    createdBy: { name: 'admin' },
    updatedAt: DateTime.now(),
    updatedBy: { name: 'admin' },
    objects: {},
  };
}

class SubGeometryDrawStore extends MapStoreBase {
  editorMode: EditorMode = EditorMode.None;
  currentMap: TLoadingState | (TLoadedState & MapDetail) = freshMap();

  get currentObject(): MapObject | undefined {
    if (!this.currentMap.isLoading && this.currentObjectId) return this.currentMap.objects[this.currentObjectId];
    return undefined;
  }

  get objectsAsArray(): MapObject[] {
    return this.currentMap.isLoading ? [] : toArray(this.currentMap.objects);
  }

  get objectsOnMainSurface(): { [key: string]: MapObject } {
    if (this.currentMap.isLoading) return {};
    if (this.currentObjectId) {
      const copy = { ...this.currentMap.objects };
      delete copy[this.currentObjectId];
      return copy;
    }
    return this.currentMap.objects;
  }

  get currentUnitSystem(): UnitSystem {
    return 'imperial';
  }

  get geometryAsMultiPolygon(): MultiPolygon | undefined {
    const objectsAsArray = this.objectsAsArray;
    if (!objectsAsArray.length) return undefined;
    return geomToMultiPolygon({ objectsAsArray, geometryType: ProjectGeometryType.Polygon });
  }

  setEditorMode(mode: EditorMode): void {
    if (mode === EditorMode.None) this.currentObjectId = undefined;
    this.editorMode = mode;
  }

  generateSortOrderKey(): string {
    return 'abc';
  }

  setCurrentObjectId(objectId: string): void {
    if (this.currentMap.isLoading) return;
    const object = this.currentMap.objects[objectId];
    if (object) {
      this.currentObjectId = object.id;
      if (object.type === 'polygon') this.setEditorMode(EditorMode.EditPolygon);
    }
  }

  resetCurrentObjectId(): void {
    this.currentObjectId = undefined;
    this.setEditorMode(EditorMode.None);
  }

  async newObject(
    o: Omit<MapObject, 'createdBy' | 'updatedBy'>,
    setCurrentObject = true,
  ): Promise<MapObject | undefined> {
    o.title = '';
    o.properties = { ...o.properties, color: 'orange' };
    set(this.currentMap, `objects.${o.id}`, o);
    if (setCurrentObject) {
      this.setEditorMode(EditorMode.None);
      setTimeout(() => this.setCurrentObjectId(o.id), 100);
    }
    return undefined;
  }

  async updateObject<T = MapObject>(o: Partial<T>): Promise<void> {
    if (!this.currentMap.isLoading && this.currentObjectId) {
      const merged = mergeWith(this.currentObject, o, compareWithArrayCopy);
      this.currentMap.objects[this.currentObjectId] = merged;
    }
  }

  async deleteObject<T = MapObject>(o: T): Promise<MapObject | undefined> {
    if (!this.currentMap.isLoading) delete this.currentMap.objects[(o as MapObject).id];
    return undefined;
  }

  startDraw(): void {
    // Enforce a single polygon per sub region: don't begin a new draw once one exists.
    if (this.objectsAsArray.length > 0) return;
    this.setEditorMode(EditorMode.DrawPolygon);
  }

  loadGeometry(geometry: Polygon | MultiPolygon): void {
    this.reset();
    if (this.currentMap.isLoading) return;
    for (const obj of geometryToPolygonObjects(geometry)) {
      set(this.currentMap, `objects.${obj.id}`, obj);
    }
  }

  reset(): void {
    this.currentMap = freshMap();
    this.currentObjectId = undefined;
    this.editorMode = EditorMode.None;
  }
}

const subGeometryDrawStore = proxy(new SubGeometryDrawStore());
export default subGeometryDrawStore;
